import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Box, LinearProgress } from '@mui/material';
import _ from 'lodash';
import deepmerge from 'deepmerge';

import useNotification from '../../../../core/hooks/use_notification';
import { getAllFoodPartnersNotificationData, getFoodPartnerOrdersNotificationData } from '../../../../core/utils/notification_utils';
import { getDefaultPaginationQueryValues } from '../../../../core/utils/api_utils';
import FoodPartnerOrdersList from './food_partner_orders_list';
import { getFoodPartnerOrdersAction, getFoodPartnerOrdersState } from '../../../../features/inventory/get_food_partner_orders';
import PaginationControls from '../../../common/pagination_controls';
import { Button } from '../../../common/button';
import { ROUTE_FOOD_PARTNER_ORDERS_NEW } from '../../../../core/constants';
import { getAllFoodPartnersAction, getAllFoodPartnersState } from '../../../../features/food_partner/get_all_food_partners';
import FoodPartnerFilters from './food_partner_filters';
import PageHeader from '../../../common/page_header';

const initialQueryParams = {
    ...getDefaultPaginationQueryValues,
    status: 'ALL',
    foodPartnerId: 'none',
    sort: ['createDate', 'desc']
};

const FoodPartnerOrdersPage = () => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();
    const navigate = useNavigate();

    const getFoodPartnerOrders = useSelector(getFoodPartnerOrdersState);
    const getAllFoodPartners = useSelector(getAllFoodPartnersState);

    const [queryParams, setQueryParams] = useState(initialQueryParams);

    const refreshList = useCallback(() => {
        const params = { ..._.omit(queryParams, [queryParams?.foodPartnerId === 'none' && 'foodPartnerId']) };

        dispatch(getFoodPartnerOrdersAction(params))
            .then(response => showErrorMessage(getFoodPartnerOrdersNotificationData(response)));
    }, [dispatch, queryParams, showErrorMessage]);

    useEffect(() => {
        refreshList();
    }, [refreshList]);

    useEffect(() => {
        dispatch(getAllFoodPartnersAction())
            .then(response => showErrorMessage(getAllFoodPartnersNotificationData(response)));
    }, [dispatch, showErrorMessage]);

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 3, mb: 4 } }>

            <PageHeader
                title="Zamówienia do dostawców"
                actions={
                    <Button onClick={ () => navigate(ROUTE_FOOD_PARTNER_ORDERS_NEW) }>
                        Dodaj zamówienie
                    </Button>
                }
            />

            {
                getAllFoodPartners?.data && (
                    <FoodPartnerFilters queryParams={ queryParams } setQueryParams={ setQueryParams }/>
                )
            }

            {
                getFoodPartnerOrders?.loading && (
                    <Box sx={ { width: '100%' } }>
                        <LinearProgress/>
                    </Box>
                )
            }

            {
                getFoodPartnerOrders?.data?.content && (
                    <Box sx={ { display: 'flex', flexDirection: 'column', gap: 2 } }>
                        <FoodPartnerOrdersList refreshList={ refreshList }/>

                        <Box sx={ { display: 'flex', justifyContent: 'flex-end', mt: 1 } }>
                            <PaginationControls
                                currentPage={ getFoodPartnerOrders?.data?.pageable?.pageNumber }
                                totalPages={ getFoodPartnerOrders?.data?.totalPages }
                                setPage={ page => setQueryParams(prevState => deepmerge(prevState, { page })) }
                            />
                        </Box>
                    </Box>
                )
            }
        </Box>
    );
};

export default FoodPartnerOrdersPage;